import type { CollectionKey, PreviewData, RowDiff } from "./profile-merge-types";

// The merge review addresses EVERYTHING by key: `choices`, `edits`, `rowEdits`,
// `rowExistingEdits` and the guided review's active step all share ONE flat
// namespace in the merge store — person_detail keys plus collection rowKeys. Two
// rows arriving under one rowKey therefore alias: both render as the active guide
// step with the same counter, deciding one collapses the other, and React sees
// duplicate list keys.
//
// That is what genesis#178 produced. The rowKey used to BE the normalized
// natural-key hash, so certificates named "Vaadin" and "vaadin" — which normalize
// to the same key — shared it. genesis has since separated identity from matching:
// a rowKey is now `"<section>:<index>"`, unique per row by construction and
// explicitly opaque (the schema says not to parse it or read meaning into it),
// while the natural key only decides which saved row an incoming row matches.
//
// So against a current genesis this guard never fires, and the DEV warning below
// is best read as a CONTRACT-REGRESSION ALARM rather than an expected event. It
// stays because the store cannot verify the invariant it depends on: row-key
// uniqueness is a server-side promise, and breaking it corrupts state silently
// across cards instead of failing loudly. Suffixing later duplicates (`…#2`,
// `…#3`) keeps the review correct even then.
//
// Doing this client-side is safe because a rowKey NEVER leaves the browser: the
// commit builds canonical create/update mutations from each row's own `fields` +
// `existingRowId` (see profile-merge-commit), so the key is only ever a local
// handle. genesis additionally guarantees that no two rows of a preview carry the
// same `existingRowId`, so rows separated here can never collide on the write side
// either — a duplicate-keyed pair would otherwise commit through the single
// resolution that survived commit's by-key map.

// The five collection sections, spelled out as an exhaustive Record so adding a
// CollectionKey fails to compile here instead of silently escaping the dedup.
const COLLECTIONS: Record<CollectionKey, true> = {
  experiences: true,
  educations: true,
  languageSkills: true,
  certificates: true,
  expertises: true,
};

const COLLECTION_KEYS = Object.keys(COLLECTIONS) as CollectionKey[];

/**
 * Returns `preview` with every collection rowKey made unique across the WHOLE
 * payload — all five collections, plus the person_detail keys and any `reserved`
 * synthetic step keys they share the store's namespace with. The first row to
 * claim a key keeps it; each later claimant gets the first free `<key>#<n>`
 * (n from 2), so an incoming key that already looks suffixed can't collide either.
 *
 * Only ROWS are ever renamed: a person_detail key is load-bearing elsewhere
 * (MERGE_KEY_TO_PROFILE_KEY, DETAIL_KEY_ORDER, DETAIL_KEY_TAB) and a reserved key
 * is the guide's own, so both win any tie.
 *
 * Returns the very same object (and reuses every untouched row) when the preview
 * is already unique — the overwhelmingly common case — so the normal path costs
 * nothing and stays referentially stable.
 */
export function uniquifyRowKeys(preview: PreviewData, reserved: readonly string[] = []): PreviewData {
  const seen = new Set([...reserved, ...preview.personDetail.map((d) => d.key)]);
  const collided: string[] = [];
  const rewritten: Partial<Record<CollectionKey, RowDiff[]>> = {};

  for (const section of COLLECTION_KEYS) {
    let changed = false;
    const rows = preview[section].map((row) => {
      if (!seen.has(row.rowKey)) {
        seen.add(row.rowKey);
        return row;
      }
      let n = 2;
      while (seen.has(`${row.rowKey}#${n}`)) n += 1;
      const rowKey = `${row.rowKey}#${n}`;
      seen.add(rowKey);
      collided.push(row.rowKey);
      changed = true;
      return { ...row, rowKey };
    });
    if (changed) rewritten[section] = rows;
  }

  if (collided.length === 0) return preview;
  if (import.meta.env.DEV) {
    console.warn(
      "[profile-merge] import preview carried duplicate rowKeys; uniquified locally (genesis#178)",
      collided,
    );
  }
  return { ...preview, ...rewritten };
}
